import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import CommonImage from '@components/CommonImage';
import LoaderComponent from '@components/LoaderComponent';

import ProductModal from '@features/trend-research/components/common/ProductModal';
import AddMoodBoard from '@features/trend-research/components/moodboard/addMoodboard';
import KeyFabrics from '@features/trend-research/components/onboarding/components/KeyFabrics';
import KeyColorOrTheme from '@features/trend-research/components/onboarding/components/KeyColorOrTheme';
import KeyGraphicsOrPrint from '@features/trend-research/components/onboarding/components/KeyGraphicsOrPrint';
import { useFetchTrendExploreProductsMutation } from '@features/trend-research/api/onboardingApi';
import useTrendResearchFilterParams from '@features/trend-research/hooks/useTrendResearchFilterParams';
import EmptyState from '@features/trend-research/components/common/EmptyState';
import { getDateAgo, trackMixPanelStyleEvent } from '@features/trend-research/utils/utils';
import Tags from '@features/trend-research/components/common/Tags';

import {
	useExploreProductsSource,
	useIsSetPersonalSettings,
	useSelectedGroup,
	useSelectedSort
} from '@store/reducers/trend-research/TrendResearchReducer';

import { capitalizeFirstLetter, isPageReachBottom } from '@utils/Util';

const ExploreDesign = () => {
	const sourceType = useExploreProductsSource();
	const [products, setProducts] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const [isAddToMoodBoard, setIsAddToMoodBoard] = useState(false);
	const [selectedProductId, setSelectedProductId] = useState(null);
	const [isOpenMoodBoardModal, setIsOpenMoodBoardModal] = useState(false);
	const [selectedMoodBoardProduct, setSelectedMoodBoardProduct] = useState(null);
	const [fetchTrendExploreProducts, { data, isLoading }] = useFetchTrendExploreProductsMutation();
	const { requestBody } = useTrendResearchFilterParams();
	const selectedSort = useSelectedSort();
	const selectedGroup = useSelectedGroup();
	const [page, setPage] = useState(null);
	const isSetPersonalSettings = useIsSetPersonalSettings();

	useEffect(() => {
		setPage(0);
	}, [sourceType, requestBody, selectedSort, selectedGroup]);

	const debounce = useDebouncedCallback(() => {
		if (selectedGroup === 'DEFAULT' && selectedSort && page !== null && isSetPersonalSettings) {
			fetchTrendExploreProducts({
				source: sourceType,
				queryParams: `?page=${page}&size=40${requestBody?.search ? '&k=500' : ''}`,
				requestBody
			});
		}
	}, 200);

	const onClose = () => {
		setShowModal(false);
		setSelectedProductId(null);
	};

	useEffect(() => {
		if (data?.data?.length > 0) {
			if (data?.currentPage > 0) {
				setProducts((prev) => [...prev, ...data.data]);
			} else {
				setProducts(data.data);
			}
		}
		if (data?.currentPage === 0 && data?.data?.length === 0) {
			setProducts([]);
		}
	}, [data]);

	useEffect(() => {
		if (requestBody && selectedSort && page !== null) {
			debounce();
		}
	}, [requestBody, page, selectedSort, sourceType, isSetPersonalSettings]);

	const handleScroll = useDebouncedCallback(() => {
		if (isPageReachBottom() && !isLoading && data?.currentPage + 1 < data?.totalPages) {
			setPage((prev) => prev + 1);
		}
	}, 100);

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, [selectedGroup, requestBody, selectedSort]);

	const renderBrandLogo = (item) => {
		return (
			<div className="brand-logo">
				{item?.brandLogoDocResponse?.docUrl && (
					<CommonImage src={item?.brandLogoDocResponse?.docUrl} alt="dd" isStatic={false} />
				)}
			</div>
		);
	};

	const renderInfluencerInfo = (item) => {
		return (
			<div
				className="instram-button d-flex align-items-center justify-content-center cursor-pointer"
				onClick={(e) => {
					e.stopPropagation();
					window.open(item.postUrl, '_blank');
				}}
			>
				<CommonImage
					className="inst-img"
					src="/icons/arrow_forward_dark.svg"
					width={16}
					height={16}
					alt=""
				/>
				<CommonImage
					className="avatar mx-2"
					src={item?.userProfilePicUrl || '/images/profile_avatar.png'}
					alt=""
					isStatic={!!item?.userProfilePicUrl}
					width={18}
					height={18}
				/>
				<p className="radius weight-500 gray_dark_01">{item?.userName}</p>
			</div>
		);
	};

	const renderProducts = () => {
		if (products.length === 0) {
			return <EmptyState title="No data found" />;
		} else {
			return (
				<div className="my-masonry-grid-explore">
					{products?.map((item, idx) => {
						return (
							<div
								className="masonry-item cursor-pointer"
								key={`explore_design_${idx}`}
								onClick={() => {
									setSelectedProductId(item.id);
									setShowModal(true);
								}}
							>
								<div className="masonry-image">
									<img className="radius-4 masonry-item-image" src={item.imageUrl} alt="" />
									{item?.isBestseller && <Tags label="Bestseller" className="yellow-3" />}
									{!item?.isBestseller && item?.isNewArrival && (
										<Tags label="New" className="blue-3" />
									)}
									<div className="card-overly radius-4 d-flex flex-column justify-content-between">
										<div className="logo-and-fab d-flex justify-content-between">
											{sourceType === 'BRAND' && renderBrandLogo(item)}
											{sourceType !== 'BRAND' && renderInfluencerInfo(item)}
											<div className="custom-chekbox">
												<div className="form-group d-flex mb-0">
													{selectedMoodBoardProduct?.id !== item.id && (
														<CommonImage
															src="/icons/not-favorite.svg"
															alt="not-favourite"
															className="add-to-favorite cursor-pointer"
															onClick={async (e) => {
																e.stopPropagation();
																trackMixPanelStyleEvent(
																	item,
																	sourceType,
																	'TRENDS_ADD_TO_MOODBOARD'
																);
																await handleMoodboardClick(item);
															}}
														/>
													)}
													{selectedMoodBoardProduct?.id === item.id && (
														<CommonImage
															src="/icons/favorite.svg"
															alt="not-favourite"
															className="add-to-favorite cursor-pointer"
														/>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="card-content mt-12">
									<p className="design-title semibold-14">
										{capitalizeFirstLetter(item.name)}
									</p>
									<ul className="common-list-component mt-1">
										{item?.price > 0 && <li>${item.price}</li>}
										<li>{item.brandName}</li>
										{item?.lastDownloadDate && (
											<li>{getDateAgo(item.lastDownloadDate)}</li>
										)}
									</ul>
								</div>
							</div>
						);
					})}
				</div>
			);
		}
	};

	const handleMoodboardClick = async (product) => {
		setIsAddToMoodBoard(true);
		setIsOpenMoodBoardModal(true);
		setSelectedMoodBoardProduct(product);
	};

	const renderProductCards = () => {
		switch (selectedGroup) {
			case 'FABRIC':
				return <KeyFabrics />;
			case 'COLOR':
				return (
					<KeyColorOrTheme
						title="Key Color"
						keyType={`${sourceType.toUpperCase()}/key-color`}
						type={`key-color`}
						summaryField="noOfColors"
						source={sourceType}
					/>
				);
			case 'THEME':
				return (
					<KeyColorOrTheme
						title="Key Theme"
						keyType={`${sourceType.toUpperCase()}/key-theme`}
						type={`key-theme`}
						summaryField="noOfThemes"
						source={sourceType}
					/>
				);
			case 'GRAPHICS':
				return (
					<KeyGraphicsOrPrint
						title="Key Graphics"
						keyType={`${sourceType.toUpperCase()}/key-graphics`}
						type={`key-graphics`}
						summaryField="noOfGraphics"
						source={sourceType}
					/>
				);
			case 'AOP':
				return (
					<KeyGraphicsOrPrint
						title="Key Prints"
						keyType={`${sourceType.toUpperCase()}/key-prints`}
						type={`key-prints`}
						summaryField="noOfPrints"
						source={sourceType}
					/>
				);
			default:
				return <>{renderProducts()}</>;
		}
	};

	return (
		<LoaderComponent loading={isLoading}>
			{renderProductCards()}
			{isOpenMoodBoardModal && (
				<AddMoodBoard
					isAddToMoodBoard={isAddToMoodBoard}
					setIsAddToMoodBoard={setIsAddToMoodBoard}
					isOpenMoodBoardModal={isOpenMoodBoardModal}
					setIsOpenMoodBoardModal={setIsOpenMoodBoardModal}
					selectedProduct={selectedMoodBoardProduct}
					source={capitalizeFirstLetter(sourceType)}
				/>
			)}

			{showModal && selectedProductId && (
				<ProductModal
					onClose={onClose}
					id={selectedProductId}
					typeOfSource={capitalizeFirstLetter(sourceType)}
				/>
			)}
		</LoaderComponent>
	);
};

export default ExploreDesign;
